import type {
  CancelDocumentInput,
  CreateDocumentInput,
  CreatePaymentInput,
  CustomerAccount,
  CustomerStatement,
  Document,
  DocumentDetail,
  Paginated,
  Payment,
} from '@fdt/shared';
import { api } from '../api/client';
import type { ListParams } from '../api/resource';

function cleanParams(params: ListParams): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
  );
}

export const documentsApi = {
  async list(params: ListParams): Promise<Paginated<Document>> {
    const { data } = await api.get<Paginated<Document>>('/documents', {
      params: cleanParams(params),
    });
    return data;
  },

  async get(id: string): Promise<DocumentDetail> {
    const { data } = await api.get<DocumentDetail>(`/documents/${id}`);
    return data;
  },

  async create(body: CreateDocumentInput): Promise<DocumentDetail> {
    const { data } = await api.post<DocumentDetail>('/documents', body);
    return data;
  },

  async cancel(id: string, body: CancelDocumentInput): Promise<DocumentDetail> {
    const { data } = await api.post<DocumentDetail>(`/documents/${id}/cancel`, body);
    return data;
  },
};

export const paymentsApi = {
  async list(params: ListParams): Promise<Paginated<Payment>> {
    const { data } = await api.get<Paginated<Payment>>('/payments', {
      params: cleanParams(params),
    });
    return data;
  },

  async create(body: CreatePaymentInput): Promise<Payment> {
    const { data } = await api.post<Payment>('/payments', body);
    return data;
  },

  async cancel(id: string, reason: string): Promise<Payment> {
    const { data } = await api.post<Payment>(`/payments/${id}/cancel`, { reason });
    return data;
  },
};

export const accountsApi = {
  async get(customerId: string): Promise<CustomerAccount> {
    const { data } = await api.get<CustomerAccount>(`/customers/${customerId}/account`);
    return data;
  },

  async statement(
    customerId: string,
    range: { from?: string; to?: string } = {},
  ): Promise<CustomerStatement> {
    const { data } = await api.get<CustomerStatement>(`/customers/${customerId}/statement`, {
      params: cleanParams(range as ListParams),
    });
    return data;
  },
};
